import { useState } from "react";
import { Link } from "react-router-dom";

import { DataTable } from "../components/DataTable";
import { MonthBars } from "../components/MonthBars";
import { Card, ErrorBox, Loading, PageHeader } from "../components/Page";
import { StatTile } from "../components/StatTile";
import { day, monthLabel, num } from "../lib/format";
import { useApi } from "../lib/useApi";

interface Product {
  sku: string | null; name: string; ours: boolean; months: (number | null)[]; total: number | null; bottles: number;
  avg_month: number | null; on_hand: number | null; months_of_stock: number | null;
}
interface Market { market: string; months: (number | null)[]; total: number; stores: number }
interface Store { code: string | null; name: string | null; market: string | null; cases: number; invoices: number; products: number; first: string; last: string }
interface View {
  wholesaler: string; as_of: string | null; months: string[]; last_full_month: string | null; average_of: string[];
  products: Product[]; markets: Market[]; accounts: number[]; stores: Store[];
  stock_as_of: string | null; stock: { sku: string | null; name: string; cases: number; bottles: number }[];
  totals: { months: number[]; total: number; undated: number; avg_month: number | null; stores: number;
            on_hand: number | null; months_of_stock: number | null } | null;
}

const short = (name: string) => name.replace("Shipwreck ", "").replace(" Rum", "");
const cell = (v: number | null | undefined) => (v == null ? "" : v ? num(v) : "—");
const MARKETS: Record<string, string> = { NJ: "New Jersey", CT: "Connecticut", DE: "Delaware", DC: "Washington DC" };

/** One CSV of the product-by-month table, for a spreadsheet. */
function csv(d: View): string {
  const head = ["Product", "SKU", ...d.months, "Total (our cases)", "Average month", "OPICI on hand", "Months of stock"];
  const rows = d.products.filter((p) => p.ours).map((p) => [p.name, p.sku ?? "", ...p.months.map((v) => v ?? ""), p.total ?? "",
    p.avg_month?.toFixed(1) ?? "", p.on_hand ?? "", p.months_of_stock?.toFixed(1) ?? ""]);
  return [head, ...rows].map((r) => r.map((v) => (/[",\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : v)).join(",")).join("\n");
}

export function Depletions() {
  const { data: d, error, reload } = useApi<View>("/depletions");
  const [market, setMarket] = useState("");
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!d) return <Loading />;
  if (!d.totals) {
    return (
      <>
        <PageHeader title="Depletions" sub="Cases a wholesaler sold on to stores and bars." />
        <Card><p className="note muted">Nothing from OPICI yet. It is pulled every morning; an admin can pull it now from{" "}
          <Link to="/sources/OPICI_DEPLETIONS">Integrations</Link>.</p></Card>
      </>
    );
  }
  const t = d.totals;
  const avgLabel = d.average_of.length ? `${monthLabel(d.average_of[0])}–${monthLabel(d.average_of[d.average_of.length - 1])}` : "";
  const nowMonth = d.months[d.months.length - 1];
  const stores = market ? d.stores.filter((s) => s.market === market) : d.stores;
  const download = () => {
    const url = URL.createObjectURL(new Blob([csv(d)], { type: "text/csv" }));
    const a = Object.assign(document.createElement("a"), { href: url, download: `opici-depletions-${d.as_of}.csv` });
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <PageHeader title="Depletions"
        sub={<>What OPICI sold on to stores and bars in New Jersey, Connecticut, Delaware and DC, from their DivePort
          {d.as_of ? <> as of {day(d.as_of)}</> : null}. MHW already sold these cases once, to OPICI, so they are never added to sales.</>}
        actions={<button className="btn" onClick={download}>Download CSV</button>} />

      <section className="tiles" aria-label="Headline figures">
        <StatTile label="Cases a month" value={num(t.avg_month)} sub={`average of ${avgLabel}`} />
        <StatTile label={`${monthLabel(nowMonth)} so far`} value={num(t.months[t.months.length - 1])} sub="cases, month still running" />
        <StatTile label="Stores and bars" value={num(t.stores)} sub={`bought since ${monthLabel(d.months[0])}`} />
        <StatTile label="OPICI on hand" value={num(t.on_hand)}
          sub={t.months_of_stock != null ? `cases, about ${num(t.months_of_stock, 1)} months of selling` : "cases"}
          status={t.months_of_stock != null && t.months_of_stock < 1 ? "warning" : undefined} />
      </section>

      <Card title="Cases a month, every product">
        <MonthBars title="Cases" points={d.months.map((m, i) => ({ month: m, value: t.months[i] ?? 0, label: `${num(t.months[i])} cs` }))} />
        <p className="note muted">
          Our cases (six 750ml, or 120 minis), worked out from OPICI's own packs: their 12-packs count as two of ours and their
          3-pack "MOCO" as half. New Jersey only starts in February 2026, when OPICI moved it to their new system; Delaware and
          DC go back to September 2025.
        </p>
      </Card>

      <Card title="By product, month by month">
        <div className="table-scroll">
          <table className="grid compact" data-testid="depletion-products">
            <thead><tr><th>Product</th>{d.months.map((m) => <th key={m} className="n">{monthLabel(m)}</th>)}
              <th className="n">Total</th><th className="n">Average month</th><th className="n">OPICI on hand</th>
              <th className="n">Months of stock</th></tr></thead>
            <tbody>
              {d.products.filter((p) => p.ours).map((p) => (
                <tr key={p.sku ?? p.name}>
                  <td className="name">{p.sku ? <Link to={`/products/${p.sku}`}>{short(p.name)}</Link> : p.name}</td>
                  {p.months.map((v, i) => <td key={i} className="n">{cell(v)}</td>)}
                  <td className="n"><strong>{num(p.total)}</strong></td>
                  <td className="n">{p.avg_month == null ? "" : num(p.avg_month, 1)}</td>
                  <td className="n">{p.on_hand == null ? "" : num(p.on_hand)}</td>
                  <td className={`n${p.months_of_stock != null && p.months_of_stock < 1 ? " down" : ""}`}>
                    {p.months_of_stock == null ? "" : num(p.months_of_stock, 1)}</td>
                </tr>
              ))}
              <tr className="totals"><td>Total</td>{t.months.map((v, i) => <td key={i} className="n">{cell(v)}</td>)}
                <td className="n"><strong>{num(t.total)}</strong></td><td className="n">{num(t.avg_month, 1)}</td>
                <td className="n">{num(t.on_hand)}</td><td className="n">{t.months_of_stock == null ? "" : num(t.months_of_stock, 1)}</td></tr>
            </tbody>
          </table>
        </div>
        <p className="note muted">
          Average month: {avgLabel}, the last three months that are over. Months of stock: what OPICI hold
          {d.stock_as_of ? <> on {day(d.stock_as_of)}</> : null} divided by that average; under one month is in red.
        </p>
        {d.products.some((p) => !p.ours) && (
          <p className="note muted">
            Also sold, under OPICI's own names (no single flavour of ours to count them as):{" "}
            {d.products.filter((p) => !p.ours).map((p) => `${p.name} (${num(p.bottles)} bottles)`).join("; ")}.
          </p>
        )}
      </Card>

      <Card title="By state">
        <div className="table-scroll">
          <table className="grid compact" data-testid="depletion-markets">
            <thead><tr><th>State</th>{d.months.map((m) => <th key={m} className="n">{monthLabel(m)}</th>)}
              <th className="n">Total</th><th className="n">Stores</th></tr></thead>
            <tbody>
              {d.markets.map((m) => (
                <tr key={m.market}><td className="name">{MARKETS[m.market] ?? m.market}</td>
                  {m.months.map((v, i) => <td key={i} className="n">{cell(v)}</td>)}
                  <td className="n"><strong>{num(m.total)}</strong></td><td className="n">{num(m.stores)}</td></tr>
              ))}
              <tr className="totals"><td>Stores buying that month</td>{d.accounts.map((v, i) => <td key={i} className="n">{num(v)}</td>)}
                <td className="n" /><td className="n">{num(t.stores)}</td></tr>
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="Stores and bars" actions={
        <select aria-label="State" value={market} onChange={(e) => setMarket(e.target.value)}>
          <option value="">Every state</option>
          {d.markets.map((m) => <option key={m.market} value={m.market}>{MARKETS[m.market] ?? m.market}</option>)}
        </select>}>
        <DataTable<Store> rows={stores} rowKey={(s) => s.code ?? s.name ?? ""} testId="depletion-stores"
          filterPlaceholder="Filter by store…" initialSort={{ key: "cases", dir: "desc" }} limit={50}
          columns={[
            { key: "name", label: "Store" },
            { key: "market", label: "State" },
            { key: "cases", label: "Cases", numeric: true, render: (s) => num(s.cases, 1) },
            { key: "invoices", label: "Orders", numeric: true },
            { key: "products", label: "Products", numeric: true },
            { key: "first", label: "First order", render: (s) => day(s.first) },
            { key: "last", label: "Last order", render: (s) => day(s.last) },
          ]} />
      </Card>
    </>
  );
}
